import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
    Box,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { DataGridPro, LicenseInfo } from '@mui/x-data-grid-pro'; 
import {
    CameraAlt,
    Check,
    Undo,
} from '@mui/icons-material';

import { HasPermissions } from '../../contexts/PermissionContext';

import DataGridTextField from '../textfield/DataGridTextField';
import SettingsTextField from '../textfield/SettingsTextField';

LicenseInfo.setLicenseKey('17ca2e8476fa658618680f9f135dbc81T1JERVI6MzU2MTYsRVhQSVJZPTE4NzMzODQyMDIwMDAsS0VZVkVSU0lPTj0x',);

function TrackerSettings({ schema={}, settings = {}, setSettings = () => {} }) {

    const columns = [
        { field: 'type', headerName: '', width: 80, renderCell: (params) => (
            <Box
                display="flex"
                flexDirection="column"
                m={1}
                position='relative'
                onClick={() => console.log(params.row)}
            >
                {
                    Object.keys(settings?.sources?.[params.value] || {}).some(
                        key => settings.sources[params.value][key] !== params.row[key]
                    ) && (
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            mx={-1}
                            my={0.8}
                        >
                        <Box
                            width={5}
                            height={5}
                            borderRadius={5}
                            backgroundColor={'rgba(255, 193, 7, 1)'} // warning color (amber)
                        />
                    </Box>
                )}
                <Typography
                    variant="subtitle2"
                    fontWeight={900}
                    fontSize="12px"
                >
                    {params.value}
                </Typography>
            </Box>
        )},
        { field: 'Source', headerName: 'Source', renderCell: (params) =>
            <DataGridTextField
                value={params.value}
                onBlur={(_, value) => handleDataGridChange('sources', {
                    ...trackerSettings?.sources,
                    [params.row.type]: {
                        ...trackerSettings?.sources?.[params.row.type],
                        [params.field]: value
                    }
                })}
            />
        },
        { field: 'Hash', headerName: 'Hash', renderCell: (params) =>
            <DataGridTextField
                value={params.value}
                onBlur={(_, value) => handleDataGridChange('sources', {
                    ...trackerSettings?.sources,
                    [params.row.type]: {
                        ...trackerSettings?.sources?.[params.row.type],
                        [params.field]: value
                    }
                })}
            />
        },
        { field: 'Name', headerName: 'Name', renderCell: (params) =>
            <DataGridTextField
                value={params.value}
                onBlur={(_, value) => handleDataGridChange('sources', {
                    ...trackerSettings?.sources,
                    [params.row.type]: {
                        ...trackerSettings?.sources?.[params.row.type],
                        [params.field]: value
                    }
                })}
            />
        },
        { field: 'Path', headerName: 'Path', renderCell: (params) =>
            <DataGridTextField
                value={params.value}
                onBlur={(_, value) => handleDataGridChange('sources', {
                    ...trackerSettings?.sources,
                    [params.row.type]: {
                        ...trackerSettings?.sources?.[params.row.type],
                        [params.field]: value
                    }
                })}
            />
        },
        { field: 'Size', headerName: 'Size', renderCell: (params) =>
            <DataGridTextField
                value={params.value}
                onBlur={(_, value) => handleDataGridChange('sources', {
                    ...trackerSettings?.sources,
                    [params.row.type]: {
                        ...trackerSettings?.sources?.[params.row.type],
                        [params.field]: value
                    }
                })}
            />
        },
        { field: 'Start', headerName: 'Start', renderCell: (params) =>
            <DataGridTextField
                value={params.value}
                onBlur={(_, value) => handleDataGridChange('sources', {
                    ...trackerSettings?.sources,
                    [params.row.type]: {
                        ...trackerSettings?.sources?.[params.row.type],
                        [params.field]: value
                    }
                })}
            />
        },
    ]

    const [rows, setRows] = useState([])
    const [trackerSettings, setTrackerSettings] = useState(settings);
    const [uncommittedChanges, setUncommittedChanges] = useState(false);

    useEffect(() => {
        setTrackerSettings(settings)
    }, [settings])

    useEffect(() => {
        const sources = Object.keys(trackerSettings?.sources ?? {});
        setRows(sources.map((source) => {
            return {
                id: source,
                type: source,
                ...trackerSettings.sources?.[source]
            }
        }))

    }, [trackerSettings])


    function handleChange(field, value) {
        const updatedSettings = _.cloneDeep(trackerSettings);
        updatedSettings[field] = value;
        updateSettings(updatedSettings);
    }

    function handleDataGridChange(field, value) {
        const updatedSettings = _.cloneDeep(trackerSettings);
        updatedSettings[field] = value;
        updateSettings(updatedSettings);
    }

    function handleDiscard() {
        setTrackerSettings(settings);
    }

    function updateSettings(newSettings) {
        setTrackerSettings(newSettings);
        setSettings(newSettings);
    }

    return (
        <Box
            display="flex"
            border={'1px solid rgba(139, 142, 146, 1)'}
            borderRadius={4}
            position="relative"
            width='100%'
        >
            {
                !_.isEqual(settings, trackerSettings) && 
                <Box
                    position="absolute"
                    top={0}
                    right={0}
                    p={1}
                >
                    <IconButton
                        sx={{
                            color: 'rgba(180, 120, 120, 0.7)', // muted red
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: 'rgb(211, 47, 47)', // vivid red
                            },
                        }}
                        onClick={handleDiscard}
                    >
                        <Undo
                            sx={{
                                fontSize: '16px',
                            }}
                        />
                    </IconButton>
                    <IconButton
                        sx={{
                            color: 'rgba(120, 180, 120, 0.7)', // muted green
                            fontSize: '16px',
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: 'rgb(46, 225, 50)', // vivid green
                            },
                        }}
                    >
                        <Check
                            sx={{
                                fontSize: '16px',
                            }}
                        />
                    </IconButton>
                </Box>
            }
            <Box
                m={2}
                display="flex"
                flexDirection="column"
                gap={2}
                overflow='auto'
            >
                <Typography variant="h6">
                    Asset Tracker Settings
                </Typography>
                <Box
                    width='100%'
                    height='100%'
                >
                    <DataGridPro
                        sx={{
                            backgroundColor: '#101010',
                            borderColor: '#122218',
                            '& .MuiDataGrid-root': { cursor: 'pointer' },
                            '& .MuiDataGrid-cell:hover': { color: 'rgb(76, 146, 107)' },
                            '& .MuiDataGrid-columnSeparator': {
                                borderColor: 'green',
                                borderWidth: '2px',
                                visibility: 'visible',
                                '&:hover': { borderColor: 'rgb(76, 146, 107)' },
                            },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#101010' },
                            '& .MuiDataGrid-columnHeader': {
                                fontSize: 14,
                                color: 'white',
                                fontWeight: 'bold',
                                borderBottom: 'None',
                            },
                            '& .MuiDataGrid-row': { border: 'none' },
                            '& .MuiDataGrid-rowCount, & .MuiDataGrid-selectedRowCount': { color: 'white' },
                            '& .MuiDataGrid-cell': {
                                border: 'none',
                                color: 'white',
                                fontSize: 13,
                            },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#161618' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#202020' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(76, 146, 107, 0.1)' },
                            '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': { backgroundColor: '#101010' },
                            '& .MuiDataGrid-pinnedColumns .MuiDataGrid-cell': {
                                backgroundColor: 'inherit',
                                color: 'white',
                                fontSize: 13,
                                border: 'none',
                            },
                            '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row:nth-of-type(odd) .MuiDataGrid-cell': { backgroundColor: '#161618' },
                            '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell': { backgroundColor: '#202020' },
                            '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row:hover .MuiDataGrid-cell': { backgroundColor: 'rgba(76, 146, 107, 0.1)' },
                            // Remove all row styles if disabled-row
                            '& .MuiDataGrid-row.disabled-row': {
                                backgroundColor: 'inherit !important',
                                color: 'grey !important',
                            },
                            '& .MuiDataGrid-row.disabled-row .MuiDataGrid-cell': {
                                backgroundColor: 'inherit !important',
                                color: 'grey !important',
                                fontSize: 13,
                                border: 'none',
                            },
                            '& .MuiDataGrid-row.disabled-row:hover': {
                                backgroundColor: 'inherit !important',
                            },
                            // Add flag row style if duplicate-row
                            '& .MuiDataGrid-row.duplicate-row': {
                                backgroundColor: 'rgba(200, 0, 0, 0.1) !important',
                                color: 'grey !important',
                            },
                            '& .MuiDataGrid-row.duplicate-row .MuiDataGrid-cell': {
                                backgroundColor: 'rgba(200, 0, 0, 0.1) !important',
                                color: 'grey !important',
                                fontSize: 13,
                                border: 'none',
                            },
                            '& .MuiDataGrid-row.duplicate-row:hover': {
                                backgroundColor: 'rgba(200, 0, 0, 0.1) !important',
                            },
                        }}
                        columns={columns}
                        rows={rows}
                        hideFooter
                        autoHeight
                    />
                </Box>
                <SettingsTextField
                    description={schema?.data?.volume_keyword?.description ?? ''}
                    disabled={!HasPermissions(schema?.permissions?.write)}
                    label={schema?.data?.volume_keyword?.label ?? 'Volume Keyword'}
                    onChange={(value) => handleChange('volume_keyword', value)}
                    original={settings?.volume_keyword ?? ''}
                    value={trackerSettings?.volume_keyword ?? ''}
                />
            </Box>
        </Box>
    );
}

export default TrackerSettings;